import { useRef } from 'react';

import { isEqual } from 'lodash';
import { useTranslation } from 'next-export-i18n';
import { useSnackbar } from 'notistack';

import { YourDetailsDrawer } from '@/components';
import { usePaymentLink, useRestaurant } from '@/hooks';
import { getStatus } from '@/services';
import { useStore } from '@/store';
import {
  LinkStatus,
  PaymentElement,
  RecordStatusEnum,
  TipModeEnum,
} from '@/types';
import { calculateTip, calculateTotalAmounts } from '@/utils';
import { useQlubRouter } from '@clubpay/customer-common-module/src/hook/router';
import {
  getDiningSession,
  getSession,
} from '@clubpay/customer-common-module/src/service/session/dsi';
import {
  ConsumerTypes,
  PaymentPortal,
  getPaymentKitProps,
  useForexStore,
} from '@clubpay/payment-kit';
import { Container, Typography } from '@mui/material';

import styles from './styles.module.scss';

function PaymentOptions() {
  const { t } = useTranslation();
  const { url, routerPush, lang } = useQlubRouter();
  const id = getSession();
  const { data: restaurant } = useRestaurant();
  const { data: payment, refetch: fetchLink } = usePaymentLink({
    shouldUpdateStatus: true,
  });
  const { enqueueSnackbar } = useSnackbar();
  const forex = useForexStore();
  const selectedTip = useStore(state => state.selectedTip);
  const customTip = useStore(state => state.customTip);
  const isYourDetailsOpen = useStore(state => state.isYourDetailsOpen);
  const setIsYourDetailsOpen = useStore(state => state.setIsYourDetailsOpen);
  const setInitialLinkData = useStore(state => state.setInitialLinkData);
  const initialLink = useStore(state => state.initialLink);
  const diningSessionPayload = getDiningSession(url);
  const payHandle = useRef<any>(null);

  const dsi = diningSessionPayload?.id || '';

  const tip = calculateTip({
    remaining: payment?.bill.remaining,
    selectedTip,
    customTip,
    tipMode: restaurant?.config.paymentLinkTipMode ?? TipModeEnum.Default,
    isTipEnabled: restaurant?.config.paymentLinkTipEnabled,
  });
  const amount = calculateTotalAmounts({
    remaining: payment?.bill.remaining,
    selectedTip,
    customTip,
    tipMode: restaurant?.config.paymentLinkTipMode ?? TipModeEnum.Default,
    currencyPrecision: payment?.currencyPrecision,
    isTipEnabled: restaurant?.config.paymentLinkTipEnabled,
  }).total;
  const shouldPaymentInterrupt =
    restaurant?.config.paymentLinkCollectCustomerEmail === true ||
    restaurant?.config.paymentLinkCollectCustomerMobile === true ||
    payment?.meta?.isMultiUse ||
    restaurant?.config?.plCustomInputsPublic?.length ||
    restaurant?.country.config.plCustomInputsPublic?.length;

  async function onBeforePayment() {
    try {
      const { data } = await fetchLink();
      if (data) {
        const updateLinkData = {
          billAmount: data.bill.billAmount!,
          currency: data.currency!,
          reference: data.meta.reference || '',
          description: data.meta.description || '',
        };
        if (data.meta.status === LinkStatus.DISCARDED) {
          enqueueSnackbar(t('the-link-has-been-discarded'), {
            variant: 'error',
            autoHideDuration: 5000,
          });
          return Promise.resolve(false);
        } else if (
          initialLink &&
          initialLink.sequentialID === data.meta.sequentialID &&
          !isEqual(initialLink.data, updateLinkData)
        ) {
          enqueueSnackbar(
            t(
              'details-of-the-payment-link-was-modified-please-review-and-proceed',
            ),
            {
              variant: 'info',
              autoHideDuration: 5000,
            },
          );
          setInitialLinkData({
            sequentialID: data.meta.sequentialID,
            data: updateLinkData,
          });
          return Promise.resolve(false);
        } else if (Number(data.bill.remaining) <= 0) {
          enqueueSnackbar(t('this-bill-was-fully-paid'), {
            variant: 'warning',
            autoHideDuration: 5000,
          });
          return Promise.resolve(false);
        } else if (
          data.meta.expired ||
          (data.meta.expiration ?? Date.now()) < Date.now()
        )
          return Promise.resolve(false);
        else return Promise.resolve(true);
      }
      return Promise.reject('FORCE_FAILED');
    } catch (e) {
      if (e !== 'FORCE_FAILED')
        enqueueSnackbar(t('something-went-wrong'), {
          variant: 'error',
          autoHideDuration: 5000,
        });
      return Promise.reject('FORCE_FAILED');
    }
  }

  const onPaymentPending = async (_gatewayId: any, payload: any) => {
    const ref = payload.refId;
    try {
      if (ref) {
        const res = await getStatus({
          id,
          diningSessionID: dsi,
          reference: ref,
        });
        if (res && res.record.accepted)
          routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/success', {
            ref: payload.refId,
          });
        else if (res && res.record.status === RecordStatusEnum.failed)
          routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/fail', {
            ref,
          });
        else
          routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/pending', {
            ref,
          });
        if (res && res.record.accepted) return Promise.resolve(true);
        return Promise.resolve(false);
      } else {
        routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/fail', {
          ref,
        });
      }
    } catch (e) {
      routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/fail', {
        ref,
      });
    }
  };

  const onPaymentSuccess = async (_gatewayId: any, payload: any) => {
    const ref = payload.refId;
    if (ref) {
      await getStatus({
        id,
        diningSessionID: dsi,
        reference: ref,
      }).then(
        res => {
          if (res && res.record.accepted)
            routerPush(
              '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/success',
              {
                ref: payload.refId,
              },
            );
        },
        () => {
          enqueueSnackbar(t('something-went-wrong'), {
            variant: 'error',
            autoHideDuration: 5000,
          });
        },
      );
    }
  };

  async function onPaymentFailure(_id: any, payload: any) {
    const { data } = await fetchLink();
    if (data) {
      if (Number(data.bill.remaining) <= 0) {
        enqueueSnackbar(t('this-bill-was-fully-paid'), {
          variant: 'warning',
          autoHideDuration: 5000,
        });
        return;
      }
      const updateLinkData = {
        billAmount: data.bill.billAmount!,
        currency: data.currency!,
        reference: data.meta.reference || '',
        description: data.meta.description || '',
      };
      if (
        initialLink &&
        initialLink.sequentialID === data.meta.sequentialID &&
        !isEqual(initialLink.data, updateLinkData)
      ) {
        enqueueSnackbar(
          t(
            'details-of-the-payment-link-was-modified-please-review-and-proceed',
          ),
          {
            variant: 'info',
            autoHideDuration: 5000,
          },
        );
        return;
      }
    }

    if (payload?.error === 'PAYMENT_AMOUNT_IS_ZERO')
      enqueueSnackbar(t('this-bill-was-fully-paid'), {
        variant: 'error',
        autoHideDuration: 5000,
      });
    else routerPush('/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/invoice/fail');
  }

  function onPaymentMethodSelect(data: any) {
    payHandle.current = data.fn;
  }

  return (
    <>
      <Container className={styles.container}>
        <Typography variant='title_md'>{t('payment-method')}</Typography>
        <div className={styles.paymentWrapper}>
          <PaymentPortal
            {...getPaymentKitProps(restaurant?.paymentGatewayList.itemsList, {
              amount: Number(amount || 0),
              totalAmount: Number(amount || 0),
              vendor: restaurant,
              hasSplitPayment: false,
              enableCardPayment: true,
              diningSessionID: diningSessionPayload?.id || '',
              currencyPrecision: payment?.currencyPrecision || 2,
              tip,
              lang,
              remainingAmount: Number(amount || 0),
              consumer: ConsumerTypes.PAYMENT_LINK,
              extraConfig: {
                disableToast: true,
              },
              events: {
                onBeforePayment,
                onPaymentPending,
                onPaymentSuccess,
                onPaymentFailure,
                ...(shouldPaymentInterrupt
                  ? {
                      onPrePayment: (fn: any) => {
                        payHandle.current = fn;
                        setIsYourDetailsOpen(true);
                      },
                    }
                  : {}),
                onPaymentMethodSelect,
              },
              exchange: forex,
              apiUrl: process.env.NEXT_PUBLIC_API_HTTP_URL!,
            })}
          />
        </div>
      </Container>
      <YourDetailsDrawer
        open={isYourDetailsOpen}
        onClose={() => setIsYourDetailsOpen(false)}
        proceedPayment={payHandle.current}
        dsi={dsi}
      />
    </>
  );
}

export default PaymentOptions;
